let gvpConfig = {},
  instanceSettings = {},
  keyValues = {};

/**
 * Checks fields in config in order to know if it's previous one.
 * @param {Object} newConfig New GVP config.
 * @returns {boolean} If both configs are equal.
 */
export function isSameGvpConfig(newConfig) {
  return (
    gvpConfig.deviceId === newConfig.deviceId &&
    gvpConfig.deviceType === newConfig.deviceType &&
    gvpConfig.instanceId === newConfig.instanceId &&
    gvpConfig.url === newConfig.url &&
    gvpConfig.caUrl === newConfig.caUrl
  );
}

/**
 * Checks current cofig.
 * @returns {boolean} If current config is thee initial onee.
 */
export function isInitialGvpConfig() {
  return Object.keys(gvpConfig).length === 0;
}

/**
 * Sets current cofig.
 * @param {Object} data Current GVP config.
 */
export function setGvpData(data) {
  gvpConfig = { ...data, country: data.language.split('_')[1].toLowerCase() };
}

/**
 * Gets current cofig.
 * @returns {Object} Current GVP config.
 */
export function getGvpData() {
  return gvpConfig;
}

/**
 * Sets current cofig.
 * @param {Object} data Current GVP config.
 */
export function setInstanceSettings(data) {
  instanceSettings = data;
}

/**
 * Gets current cofig.
 * @returns {Object} Current GVP config.
 */
export function getInstanceSettings() {
  return instanceSettings;
}

/**
 * Sets current cofig.
 * @param {Object} data Current GVP config.
 */
export function setKeyValues(global, chromecast, player) {
  keyValues = {
    isBlackoutEnabled:
      global.FEATURE_TOGGLES_BY_DEVICE.BLACKOUTS_ENABLED?.[getGvpData().deviceType] || false,
    isMacrosEnabled:
      global.FEATURE_TOGGLES_BY_DEVICE.TCS_FAST_LIVE_CHANNELS_MACROS_ENABLED[
        getGvpData().deviceType
      ] || false,
    isAvodEnabled:
      global.FEATURE_TOGGLES_BY_DEVICE.TCS_FAST_AVOD_ENABLED?.[getGvpData().deviceType] || false,
    isUniqueProfileEnabled:
      global.FEATURE_TOGGLES_BY_DEVICE.UNIQUE_PROFILE_ENABLED?.[getGvpData().deviceType] ||
      global.FEATURE_TOGGLES_BY_DEVICE.UNIQUE_PROFILE_ENABLED?.default ||
      false,
    isPauseTimeLimitEnabled:
      global.FEATURE_TOGGLES_BY_DEVICE.LIVETV_PAUSE_TIME_LIMIT_ENABLED?.[getGvpData().deviceType] ||
      global.FEATURE_TOGGLES_BY_DEVICE.LIVETV_PAUSE_TIME_LIMIT_ENABLED?.default ||
      false,
    parentalPinNoCached:
      global.FEATURE_TOGGLES_BY_DEVICE.PARENTAL_PIN_NO_CACHE_ENABLED?.[getGvpData().deviceType] ||
      global.FEATURE_TOGGLES_BY_DEVICE.PARENTAL_PIN_NO_CACHE_ENABLED?.default ||
      false,
    isDolbyDigitalPlusEnabled:
      global.FEATURE_TOGGLES_BY_DEVICE.DOLBY_DIGITAL_PLUS_ENABLED?.[getGvpData().deviceType] ??
      global.FEATURE_TOGGLES_BY_DEVICE.DOLBY_DIGITAL_PLUS_ENABLED?.default ??
      true,
    tcdnFilterName: global.UNIQUE_PROFILE_DEVICES.TCDN_FILTER_NAME,
    tcdnSUBSD:
      global.UNIQUE_PROFILE_DEVICES.TCDN_LIVE_SUBSD_BY_DEVICE?.[getGvpData().deviceType] ||
      global.UNIQUE_PROFILE_DEVICES.TCDN_LIVE_SUBSD_BY_DEVICE?.default,
    tcdnSD:
      global.UNIQUE_PROFILE_DEVICES.TCDN_LIVE_SD_BY_DEVICE?.[getGvpData().deviceType] ||
      global.UNIQUE_PROFILE_DEVICES.TCDN_LIVE_SD_BY_DEVICE?.default,
    tcdnHD:
      global.UNIQUE_PROFILE_DEVICES.TCDN_LIVE_HD_BY_DEVICE?.[getGvpData().deviceType] ||
      global.UNIQUE_PROFILE_DEVICES.TCDN_LIVE_HD_BY_DEVICE?.default,
    cdnBuffer: global.PLAYBACK.BUFFER_CDN,
    audiosOrder: global.LANGUAGE_ORDER.AUDIO.split(','),
    subsOrder: global.LANGUAGE_ORDER.SUBTITLE.split(','),
    integrationLevel: chromecast.ANALYTICS.YOUBORA_INTEGRATION_LEVEL,
    spotlightUrl: `${global.GENERAL.SPOTLIGHT_HTTPS_URL}/customer/v1/source`,
    heartBeatWindow: global.PLAYBACK.HEARTBEAT_WINDOW,
    startVideoThreshold: player.RESUME.LOWER_BOUND,
    endVideoThreshold: player.RESUME.UPPER_BOUND,
    urlPlaceholder: global.PLAYBACK.URL_PLACEHOLDER,
    postPaddingTime: global.STARTOVER?.POSTPADDING_TIME * 60 || 1800,
    widevineUrl:
      global.DRM_ISSUERS_URL_BY_DEVICE.WIDEVINE[gvpConfig.deviceType] ||
      global.DRM_ISSUERS_URL_BY_DEVICE.WIDEVINE.default ||
      gvpConfig.widevineUrl,
    playreadyUrl:
      global.DRM_ISSUERS_URL_BY_DEVICE.PLAYREADY[gvpConfig.deviceType] ||
      global.DRM_ISSUERS_URL_BY_DEVICE.PLAYREADY.default,
    dashAutoResumeDuration: chromecast.PLAYER?.DASH_AUTO_RESUME_DURATION || 2,
    dashAutoResumeNumberOfSegments: chromecast.PLAYER?.DASH_AUTO_RESUME_NUMBER_OF_SEGMENTS || 1,
    dashInitialBandwidth: chromecast.PLAYER?.DASH_INITIAL_BANDWIDTH || 1000,
    dashManifestTimeout: chromecast.PLAYER?.DASH_MANIFEST_TIMEOUT || 30000,
    dashLicenseTimeout: chromecast.PLAYER?.DASH_DRM_TIMEOUT || 30000,
    dashFragmentsTimeout: chromecast.PLAYER?.DASH_FRAGMENTS_TIMEOUT || 30000,
    presentationDelay: chromecast.PLAYER.PRESENTATION_DELAY || 8000,
    dashSwitchInterval: chromecast.PLAYER.DASH_SWITCH_INTERVAL || 4,
    svodDelayDash: chromecast.PLAYER.SVOD_DELAY_DASH || 0,
    appNameTCS: global.TCS.MACRO_APP_NAME?.[getGvpData().deviceType] || '',
    environmentTCS: global.TCS.ENVIRONMENT?.[getGvpData().deviceType] || 'ctv',
    domainTCS: global.TCS.MACRO_DOMAIN?.[getGvpData().deviceType] || '',
    appBuncleTCS: global.TCS.MACRO_DOMAIN?.[getGvpData().deviceType] || '',
    appStoreUrlTCS: global.TCS.APP_STORE_URL?.[getGvpData().deviceType] || '',
    ifaTypeTCS: global.TCS.IFA_TYPE?.[getGvpData().deviceType] || 'sessionId'
  };
}

/**
 * Gets current cofig.
 * @returns {Object} Current GVP config.
 */
export function getKeyValues() {
  return keyValues;
}
